import * as React from 'react';
import { EventDetails, HandleEventDetailsChange } from './eventDetailsFormTypes';

const getISOStringFromLocalParts = (dateOnlyPart: string, timeOnlyPart: string) => {
  return new Date(`${dateOnlyPart}T${timeOnlyPart}:00`).toISOString();
};

const getLocalDateFromISOString = (isoDateStr: string) => {
  const isoDate = new Date(isoDateStr);
  const year = isoDate.getFullYear();
  const month = String(isoDate.getMonth() + 1).padStart(2, '0');
  const day = String(isoDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLocalTimeFromISOString = (isoDate: string) => {
  return new Date(isoDate).toTimeString().substring(0, 5);
};

export const useEventDateTimeFields = <T extends EventDetails>(params: {
  startDateTime: string;
  endDateTime: string;
  isAtSpecificTime: boolean;
  handleDetailsChange: HandleEventDetailsChange<T>;
}) => {
  const { startDateTime, endDateTime, isAtSpecificTime, handleDetailsChange } = params;

  const [datePart, setDatePart] = React.useState<string>(() =>
    getLocalDateFromISOString(startDateTime || new Date().toISOString()),
  );
  const [startTime, setStartTime] = React.useState<string>(() =>
    startDateTime ? getLocalTimeFromISOString(startDateTime) : '09:00',
  );
  const [endTime, setEndTime] = React.useState<string>(() =>
    endDateTime ? getLocalTimeFromISOString(endDateTime) : '',
  );
  const [endTimeError, setEndTimeError] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (!isAtSpecificTime) return;
    if (startDateTime) {
      setDatePart(getLocalDateFromISOString(startDateTime));
      setStartTime(getLocalTimeFromISOString(startDateTime));
    }
    if (endDateTime) {
      setEndTime(getLocalTimeFromISOString(endDateTime));
    }
  }, [isAtSpecificTime, startDateTime, endDateTime]);

  const validateTimes = React.useCallback((start: string, end: string) => {
    setEndTimeError(!!start && !!end && start > end ? 'cannot be earlier than start' : undefined);
  }, []);

  const handleDateChange = React.useCallback(
    (dateOnly: string) => {
      if (!dateOnly) return;
      setDatePart(dateOnly);
      if (startTime) handleDetailsChange('start_date' as keyof T, getISOStringFromLocalParts(dateOnly, startTime));
      if (endTime) handleDetailsChange('end_date' as keyof T, getISOStringFromLocalParts(dateOnly, endTime));
    },
    [endTime, handleDetailsChange, startTime],
  );

  const handleStartTimeChange = React.useCallback(
    (time: string) => {
      setStartTime(time);
      validateTimes(time, endTime);
      if (datePart && time) handleDetailsChange('start_date' as keyof T, getISOStringFromLocalParts(datePart, time));
    },
    [datePart, endTime, handleDetailsChange, validateTimes],
  );

  const handleEndTimeChange = React.useCallback(
    (time: string) => {
      setEndTime(time);
      validateTimes(startTime, time);
      if (datePart && time) handleDetailsChange('end_date' as keyof T, getISOStringFromLocalParts(datePart, time));
    },
    [datePart, handleDetailsChange, startTime, validateTimes],
  );

  return {
    datePart,
    startTime,
    endTime,
    endTimeError,
    handleDateChange,
    handleStartTimeChange,
    handleEndTimeChange,
  };
};
